import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { reportsService, groupService } from "@/services";
import type { GroupPerformanceReport, KametiGroup } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { ErrorState } from "@/components/base/ErrorState";
import { EmptyState } from "@/components/base/EmptyState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatINR, formatINRNumber } from "@/utils/format";
import { useThemeColors } from "@/hooks/useThemeColors";
import { exportReportCSV, csvAmount } from "@/utils/report";
import { MemberRemarksModal, RemarksCell } from "@/pages/groups/detail/components/MemberRemarksModal";
import type { RemarksTarget } from "@/pages/groups/detail/components/remarkUtils";

export default function GroupPerformancePage() {
  const theme = useThemeColors();
  const { isViewer } = useAuth();
  const [remarksTarget, setRemarksTarget] = useState<RemarksTarget | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const paramGroupId = searchParams.get("groupId") || searchParams.get("id") || "";
  const [groups, setGroups] = useState<KametiGroup[]>([]);
  const [groupId, setGroupId] = useState(paramGroupId);
  const [report, setReport] = useState<GroupPerformanceReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadGroups = useCallback(async () => {
    setError(null);
    try {
      const list = (await groupService.listGroups()).filter((g) => g.status === "active" || g.status === "completed");
      setGroups(list);
      setGroupId((prev) => {
        const fromUrl = searchParams.get("groupId") || searchParams.get("id");
        if (fromUrl && list.some((g) => g.id === fromUrl)) return fromUrl;
        return prev && list.some((g) => g.id === prev) ? prev : (list[0]?.id || "");
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load groups.");
      setGroups([]);
    }
  }, [searchParams]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleGroupChange = (newId: string) => {
    setGroupId(newId);
    if (newId) {
      setSearchParams({ groupId: newId });
    } else {
      setSearchParams({});
    }
  };

  const load = useCallback(async () => {
    if (!groupId) return;
    setError(null);
    try {
      setReport(await reportsService.getGroupPerformance(groupId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the group performance report.");
      setReport(null);
    }
  }, [groupId]);

  useEffect(() => {
    load();
  }, [load]);

  // Remarks can only be added to active groups (the report also lists completed ones).
  const canEditRemarks = !isViewer && report?.group.status === "active";

  const trend = useMemo(() => report?.cycles.filter((c) => c.winnerName !== "—").map((c) => ({ name: `C${c.cycle}`, discount: c.discount })) ?? [], [report]);

  const handleExport = () => {
    if (!report) return;
    exportReportCSV({
      filename: `group-performance-${report.group.groupCode}`,
      reportName: `Group Performance — ${report.group.name}`,
      filters: [`Group ${report.group.groupCode}`],
      columns: [
        { header: "Cycle", value: (r) => r.cycle },
        { header: "Period", value: (r) => r.period },
        { header: "Winner", value: (r) => r.winner },
        { header: "Winning bid", value: (r) => csvAmount(r.bid) },
        { header: "Discount", value: (r) => csvAmount(r.discount) },
        { header: "Dividend", value: (r) => csvAmount(r.dividend) },
        { header: "Collected", value: (r) => csvAmount(r.collected) },
        { header: "Expected", value: (r) => csvAmount(r.expected) },
        { header: "Collection %", value: (r) => `${r.pct}%` },
      ],
      rows: report.cycles.map((c) => ({
        cycle: c.cycle,
        period: c.periodLabel,
        winner: c.winnerName,
        bid: c.winningBid,
        discount: c.discount,
        dividend: c.perMemberDividend,
        collected: c.collected,
        expected: c.expected,
        pct: c.collectionPct.toFixed(1),
      })),
    });
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Group performance" isLast />
          </>
        }
        title="Group performance"
        subtitle="One group in full detail — every cycle, collection performance and member standing."
        actions={
          <Button variant="outline" size="sm" icon={<i className="ri-download-2-line" />} onClick={handleExport}>
            Export
          </Button>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select value={groupId} onChange={(e) => handleGroupChange(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/40">
          <option value="">Select group</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>{g.name} ({g.groupCode})</option>
          ))}
        </select>
      </div>

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!report ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <>
          {/* Config */}
          <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            <ConfigCell label="Installment" value={formatINR(report.group.monthlyInstallment)} />
            <ConfigCell label="Members" value={String(report.group.memberCount)} />
            <ConfigCell label="Chit value" value={formatINR(report.group.chitValue)} />
            <ConfigCell label="Started" value={formatDate(report.group.startDate)} />
            <ConfigCell label="Auction day" value={String(report.group.auctionDay)} />
            <ConfigCell label="Current outstanding" value={formatINR(report.currentOutstanding)} danger={report.currentOutstanding > 0} />
          </div>

          {/* Discount trend */}
          <Card className="mb-3" title="Discount across cycles" subtitle="A healthy group's discount starts high and falls as fewer members remain to compete.">
            {trend.length === 0 ? (
              <EmptyState icon="ri-bar-chart-line" title="No decided cycles yet" compact />
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trend} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke={theme.grid} vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: theme.text }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: theme.text }} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => `₹${Math.round(v / 1000)}k`} />
                  <Tooltip
                    cursor={{ fill: theme.primary200 }}
                    formatter={(value) => [formatINRNumber(Number(value)), "Discount"]}
                    contentStyle={{ borderRadius: 8, border: "none", fontSize: 12, background: theme.tooltipBg, color: theme.tooltipText }}
                    labelStyle={{ color: theme.tooltipText }}
                  />
                  <Bar dataKey="discount" name="Discount" fill={theme.accent} radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          {/* Cycles */}
          <Card className="mb-3" title="Cycle performance">
            <div className="overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-background-100/80 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-3 py-2 text-left">Cycle</th>
                    <th className="px-3 py-2 text-left">Period</th>
                    <th className="px-3 py-2 text-left">Winner</th>
                    <th className="px-3 py-2 text-right">Winning bid</th>
                    <th className="px-3 py-2 text-right">Discount</th>
                    <th className="px-3 py-2 text-right">Dividend</th>
                    <th className="px-3 py-2 text-right">Collected</th>
                    <th className="px-3 py-2 text-right">Expected</th>
                    <th className="px-3 py-2 text-right">Collection %</th>
                    <th className="px-3 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.cycles.map((c) => (
                    <tr key={c.cycle} className="border-t border-background-100 hover:bg-background-100/50">
                      <td className="tnum px-3 py-2">{c.cycle}{c.cycle === report.group.reservedCycle ? <Badge color="accent" className="ml-1">Reserved</Badge> : null}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-foreground-600">{c.periodLabel}</td>
                      <td className="px-3 py-2 text-foreground-900">{c.winnerName || <span className="text-foreground-400">—</span>}</td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={c.winningBid} hideSymbol /></td>
                      <td className="px-3 py-2 text-right text-success-600"><CurrencyDisplay amount={c.discount} hideSymbol /></td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={c.perMemberDividend} hideSymbol /></td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={c.collected} hideSymbol /></td>
                      <td className="px-3 py-2 text-right text-foreground-600"><CurrencyDisplay amount={c.expected} hideSymbol /></td>
                      <td className="tnum px-3 py-2 text-right font-medium text-foreground-800">{c.collectionPct.toFixed(1)}%</td>
                      <td className="px-3 py-2"><Badge color={c.status === "completed" ? "success" : "warning"}>{c.status === "completed" ? "Completed" : "Pending"}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Members */}
          <Card title="Member standing" subtitle="Who has won, who is waiting, and each member's outstanding balance.">
            <div className="overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-background-100/80 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-3 py-2 text-left">Member</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Phone</th>
                    <th className="px-3 py-2 text-left">Ticket</th>
                    <th className="px-3 py-2 text-left">Won</th>
                    <th className="px-3 py-2 text-left">Remarks</th>
                    <th className="px-3 py-2 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {report.members.map((m) => (
                    <tr key={m.groupMemberId ?? m.memberId} className="border-t border-background-100 hover:bg-background-100/50">
                      <td className="px-3 py-2 font-medium text-foreground-900">{m.memberName}</td>
                      <td className="tnum px-3 py-2 text-foreground-500">{m.memberCode}</td>
                      <td className="tnum px-3 py-2 text-foreground-600">{m.memberPhone || "—"}</td>
                      <td className="tnum px-3 py-2 text-foreground-600">{m.ticketNumber}</td>
                      <td className="px-3 py-2">{m.wonCycle != null ? <Badge color="success">Cycle {m.wonCycle}</Badge> : <span className="text-foreground-400">Waiting</span>}</td>
                      <td className="px-3 py-2">
                        {m.groupMemberId ? (
                          <RemarksCell
                            remarks={m.remarks ?? []}
                            canEdit={canEditRemarks && Boolean(m.isActiveEnrollment)}
                            onOpen={() =>
                              setRemarksTarget({
                                groupMemberId: m.groupMemberId!,
                                memberName: m.memberName,
                                ticketNo: m.ticketNumber,
                                remarks: m.remarks ?? [],
                              })
                            }
                          />
                        ) : (
                          <span className="text-foreground-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={m.outstanding} hideSymbol tone={m.outstanding > 0 ? "danger" : "default"} /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold">
                    <td className="px-3 py-2 text-foreground-500" colSpan={6}>Total outstanding</td>
                    <td className="px-3 py-2 text-right"><CurrencyDisplay amount={report.currentOutstanding} hideSymbol /></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          <MemberRemarksModal
            open={Boolean(remarksTarget)}
            groupId={report.group.groupId}
            target={remarksTarget}
            readOnly={!canEditRemarks || !report.members.find((m) => m.groupMemberId === remarksTarget?.groupMemberId)?.isActiveEnrollment}
            onClose={() => setRemarksTarget(null)}
            onChanged={load}
          />
        </>
      )}
    </div>
  );
}

function ConfigCell({ label, value, danger }: { label: string; value: string; danger?: boolean }) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-3">
      <p className="text-xs text-foreground-500">{label}</p>
      <p className={`tnum mt-1 text-lg font-semibold ${danger ? "text-danger-600" : "text-foreground-950"}`}>{value}</p>
    </div>
  );
}